import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const AuthContext = createContext(null);

// An optional ?demo_mode=lister query param lets a demo build start in lister
// mode (used by integration tests and quick previews). It never affects real,
// authenticated sessions — demoUser() is only ever the offline/demo fallback.
function demoModeFromQuery() {
  if (typeof window === "undefined") return null;
  try {
    const m = new URLSearchParams(window.location.search).get("demo_mode");
    return m === "lister" ? "lister" : m === "renter" ? "renter" : null;
  } catch {
    return null;
  }
}

function demoUser(payload = {}) {
  const mode = payload.mode || demoModeFromQuery() || "renter";
  const lister = mode === "lister";
  return {
    id: "demo_user",
    name: payload.name || "Demo User",
    email: payload.email || "demo@swiprent.com",
    role: payload.role || (lister ? "lister" : "renter"),
    roles: lister ? ["renter", "lister"] : ["renter"],
    active_mode: mode,
    // A demo lister is a private lister so verification + host surfaces show.
    lister_kind: lister ? "private" : undefined,
    onboarded_as_lister: lister || undefined,
    verified: true,
    demo: true,
  };
}

// A hard "backend unreachable" failure: no HTTP response at all AND not a
// timeout. Timeouts (axios ECONNABORTED/ETIMEDOUT) mean the backend exists but
// the connection is slow — they must NEVER fake a demo login for real users.
function isBackendOffline(error) {
  if (error?.response) return false;
  const code = error?.code;
  if (code === "ECONNABORTED" || code === "ETIMEDOUT") return false;
  if (/timeout/i.test(error?.message || "")) return false;
  return true;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Mirror of `user` so stable callbacks ([] deps) can read the CURRENT
  // session without going stale — used to keep sessions alive on transient
  // refresh failures and to gate the offline demo fallback.
  const userRef = useRef(null);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Returns the fresh user (or null on failure) so callers that just saved
  // something can read profile_complete etc. without racing the state update.
  const refresh = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      const next = res.data?.user ?? res.data ?? null;
      setUser(next);
      return next;
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        // The backend explicitly rejected the session — really signed out.
        // Keep demo sessions alive (they have no server session to reject).
        setUser((u) => (u?.demo ? u : null));
        return null;
      }
      // Network error / timeout / 5xx: transient. Do NOT log the user out —
      // keep the current session and let a later refresh settle it.
      return userRef.current;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Two-step login: when the backend answers {otp_required, otp_token} the
  // caller must collect the emailed one time code and call confirmOtp. The
  // return shape is either a user object or {otpRequired, otpToken, emailHint}.
  const login = useCallback(async (email, password) => {
    try {
      const res = await api.post("/auth/login", { email, password });
      if (res.data?.otp_required) {
        return {
          otpRequired: true,
          otpToken: res.data.otp_token,
          emailHint: res.data.email_hint,
        };
      }
      const next = res.data?.user ?? res.data;
      setUser(next);
      toast.success("Welcome back to SwipRent");
      return next;
    } catch (error) {
      // Demo fallback ONLY when the backend is truly unreachable (no response,
      // not a timeout) AND there is no real authenticated session to protect.
      const hasRealSession = userRef.current && !userRef.current.demo;
      if (isBackendOffline(error) && !hasRealSession) {
        const demo = demoUser({ email });
        setUser(demo);
        toast.info("Backend is offline. Browsing SwipRent in demo mode.");
        return demo;
      }
      throw error;
    }
  }, []);

  // Complete a two-step login with the emailed code.
  const confirmOtp = useCallback(async (otpToken, code) => {
    const res = await api.post("/auth/otp", { otp_token: otpToken, code });
    const next = res.data?.user ?? res.data;
    setUser(next);
    toast.success("Welcome back to SwipRent");
    return next;
  }, []);

  const register = useCallback(async (payload) => {
    try {
      const res = await api.post("/auth/register", payload);
      const next = res.data?.user ?? res.data;
      setUser(next);
      toast.success("Account created. Welcome to SwipRent");
      return next;
    } catch (error) {
      // Same strict gate as login: never fake an account on a slow connection
      // or over an existing real session.
      const hasRealSession = userRef.current && !userRef.current.demo;
      if (isBackendOffline(error) && !hasRealSession) {
        const demo = demoUser(payload);
        setUser(demo);
        toast.info("Backend is offline. Browsing SwipRent in demo mode.");
        return demo;
      }
      throw error;
    }
  }, []);

  const logout = useCallback(async (message) => {
    // Mobile app: forget this device's push token BEFORE the cookie dies,
    // so a shared phone stops getting the previous user's notifications.
    // No-op on the website.
    try {
      const { unregisterPush } = await import("@/lib/push");
      await unregisterPush();
    } catch {
      // Push cleanup is best effort; never block signing out.
    }
    try {
      await api.post("/auth/logout");
    } catch {
      // Ignore network failures on logout — clear the session locally anyway.
    }
    setUser(null);
    // `message` may be an event object if logout is used directly as a click
    // handler — only honor a real string, else the default farewell.
    toast.success(typeof message === "string" && message ? message : "Signed out. See you soon.");
  }, []);

  // Auto sign-out after 30 minutes of inactivity: an unattended device must not
  // stay logged in. Any real interaction resets the timer. Demo sessions (an
  // offline showcase) are exempt so a demo never gets kicked mid-tour.
  useEffect(() => {
    if (!user || user.demo) return undefined;
    const IDLE_MS = 30 * 60 * 1000;
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        logout("You were signed out after 30 minutes of inactivity.");
      }, IDLE_MS);
    };
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click", "visibilitychange"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [user, logout]);

  // Switch between renter and lister mode. The backend adds the role when the
  // account does not have it yet, so one endpoint covers both cases. The mode
  // response now carries the SAME persisted onboarding fields as /auth/me
  // (lister_kind, realtor_status, onboarded_as_lister, onboarded_as_renter),
  // so we MERGE it onto the current user — never replace — to guarantee no
  // onboarding flag is ever dropped by a switch (that omission was the cause of
  // the "chooser re-asks every time" bug).
  const switchMode = useCallback(async (mode) => {
    try {
      const res = await api.post("/users/me/mode", { mode });
      const next = res.data?.user ?? res.data;
      if (next && typeof next === "object") {
        setUser((u) => (u ? { ...u, ...next } : next));
      }
      return next;
    } catch (error) {
      if (!error.response) {
        // Backend offline — flip locally so browsing keeps working.
        let flipped = null;
        setUser((u) => {
          if (!u) return u;
          flipped = {
            ...u,
            active_mode: mode,
            role: mode === "lister" ? "lister" : "renter",
            roles: Array.from(new Set([...(u.roles || [u.role].filter(Boolean)), mode])),
          };
          return flipped;
        });
        return flipped;
      }
      throw error;
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, confirmOtp, register, logout, refresh, setUser, switchMode }}>
      {children}
    </AuthContext.Provider>
  );
}

const LISTER_ROLES = ["lister", "realtor", "lister_private", "lister_realtor"];

// Current mode of the account: honors active_mode from /auth/me, falls back
// to the legacy single role field.
export function userMode(user) {
  if (!user) return null;
  if (user.active_mode) return user.active_mode === "renter" ? "renter" : "lister";
  return LISTER_ROLES.includes(user.role) ? "lister" : "renter";
}

// Whether the account already holds a role ("renter" or "lister"). Checks the
// roles array AND the legacy single role field together — accounts created
// before the roles array existed only carry user.role, and some backends fill
// roles without mirroring the legacy field.
export function userHasRole(user, mode) {
  if (!user) return false;
  const roles = [...(Array.isArray(user.roles) ? user.roles : []), user.role].filter(Boolean);
  if (mode === "lister") return roles.some((r) => LISTER_ROLES.includes(r));
  return roles.includes("renter");
}

// Whether the account has finished the ONE-TIME lister onboarding and may
// enter lister mode instantly (no chooser). Prefers the persisted backend flag
// (onboarded_as_lister); falls back to lister_kind/realtor_status for older
// payloads. Demo sessions are treated as onboarded so browsing never stalls.
export function isOnboardedLister(user) {
  if (!user) return false;
  if (user.demo || user.role === "admin") return true;
  if (typeof user.onboarded_as_lister === "boolean") return user.onboarded_as_lister;
  if (user.lister_kind === "private") return true;
  // Realtor approval gating is deactivated: realtors onboard like private
  // listers (no admin approval required to enter lister mode / post).
  if (user.lister_kind === "realtor") return true;
  return false;
}

// Whether the account has finished the ONE-TIME regular renter onboarding
// (renter profile questions). Prefers the persisted backend flag; falls back to
// a complete profile signal. Demo sessions count as onboarded.
export function isOnboardedRenter(user) {
  if (!user) return false;
  if (user.demo || user.role === "admin") return true;
  if (typeof user.onboarded_as_renter === "boolean") return user.onboarded_as_renter;
  if (user.renter_onboarded) return true;
  // A completed profile (in renter mode) means onboarding already happened.
  return userMode(user) === "renter" && user.profile_complete === true;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    return {
      user: null,
      loading: false,
      login: async () => null,
      confirmOtp: async () => null,
      register: async () => null,
      logout: async () => {},
      refresh: async () => {},
      setUser: () => {},
      switchMode: async () => null,
    };
  }
  return ctx;
}
